'use strict';

import axios from 'axios';
import store from '@/store';
import { generateStorageKey } from '@/utils';
import { APP_TOKEN_NAME } from '@/config/constants';
import router from '@/router';
import i18n from '@/i18n';

const apiUrl = process.env.VUE_APP_API_URL;

const Service = axios.create({
    baseURL: apiUrl,
    headers: {
        Accept: 'application/json'
    }
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });

    failedQueue = [];
};

Service.interceptors.request.use(
    (config) => {
        const accessToken = window.$cookies.get(generateStorageKey(APP_TOKEN_NAME));

        if (accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error.response || error.message);
    }
);

Service.interceptors.response.use(
    (response) => {
        return response;
    },
    async (err) => {
        let originalRequest = err.config;
        if (err.response.status === 401 && err?.response?.data?.message === 'auth.expired' && !originalRequest._retry) {
            try {
                if (isRefreshing) {
                    return new Promise(function (resolve, reject) {
                        failedQueue.push({ resolve, reject });
                    })
                        .then(async (token) => {
                            originalRequest.headers['Authorization'] = `Bearer ${token}`;
                            return axios(originalRequest);
                        })
                        .catch((err) => {
                            return Promise.reject(err);
                        });
                }
                originalRequest._retry = true;
                isRefreshing = true;
                const newToken = await refreshAccessToken();
                if (newToken) {
                    isRefreshing = false;
                    originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                    processQueue(null, newToken);
                    return axios(originalRequest);
                }
            } catch (_error) {
                processQueue(_error, null);
            }
        }

        return errorResponseCallback(err);
    }
);

let errorResponseCallback = (error) => {
    switch (error?.response?.status) {
        case 401:
            switch (error?.response?.data?.message) {
                case 'auth.expired':
                    break;
                case 'auth.failed':
                default:
                    store.commit('auth/logout');
                    store.commit('noticeSystem/SET_MESSAGE', {
                        isShowMessage: true,
                        type: 'error',
                        message: i18n.tc(error?.response?.data?.message)
                    });
                    sessionStorage.clear();
                    window.$cookies.keys().forEach((cookie) => window.$cookies.remove(cookie));
                    localStorage.clear();
                    router.push('/login').catch(() => {});
            }
            break;
        case 400:
        case 429:
            if (error?.response?.data?.message) {
                store.commit('noticeSystem/SET_MESSAGE', {
                    isShowMessage: true,
                    type: 'error',
                    message: i18n.tc(error?.response?.data?.message)
                });
            }
            break;
        case 422:
            if (error?.response?.data?.message) {
                store.commit('noticeSystem/SET_MESSAGE', {
                    isShowMessage: true,
                    type: 'error',
                    message: i18n.tc(error?.response?.data?.validation_key)
                });
            }
            break;
        case 403:
            break;
        case 500:
            store.commit('noticeSystem/SET_CONFIRM', {
                type: `warning`,
                isShowConfirm: true,
                message: 'error.system_error'
            });
            break;
        default:
            break;
    }
    return Promise.reject(error);
};

function getNewRefreshToken() {
    return axios
        .post(
            `${process.env.VUE_APP_API_URL}/user/auth/refresh`,
            {},
            {
                headers: {
                    Authorization: `Bearer ${window.$cookies.get(generateStorageKey(APP_TOKEN_NAME))}`
                }
            }
        )
        .catch(function (error) {
            return errorResponseCallback(error);
        });
}

const refreshAccessToken = async function () {
    const newRefreshToken = await getNewRefreshToken();

    const newToken = newRefreshToken.data.data.token;
    if (newToken) {
        window.$cookies.set(generateStorageKey(APP_TOKEN_NAME), newToken);
        return newToken;
    }
    return null;
};

export default Service;
